import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import {
  Users, Target, IndianRupee, FileText, Warehouse, ShoppingCart, Factory,
  Truck, Wrench, Headphones, MapPin, FolderKanban, Workflow, Sparkles,
  Phone, BarChart3, Plug, Search, ChevronDown, ChevronUp, LayoutGrid,
  Calculator,
  type LucideIcon,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Settings → Modules Explorer
//
// A navigational discovery grid for every module route in AURA. Different from
// the existing `Modules` tab which toggles feature flags — this one helps the
// user FIND routes that aren't in their role's sidebar.
//
// Data source: `tenant.enabledModules` from auth store. No backend calls.
// Route list: hardcoded below. Cross-checked against App.tsx (Apr 22 2026).
// ─────────────────────────────────────────────────────────────────────────────

interface ModuleRoute {
  to: string;
  label: string;
}

interface ModuleGroup {
  key: string;          // matches tenant.enabledModules entry
  label: string;
  icon: LucideIcon;
  routes: ModuleRoute[];
}

// Module groups aligned with PLAN.md "Settings → Modules matrix" spec.
// `key` matches entries in `tenant.enabledModules` — if not present, the group
// is shown as "Disabled" (but still surfaced so admin can discover + request).
const MODULE_GROUPS: ModuleGroup[] = [
  {
    key: 'crm',
    label: 'CRM',
    icon: Users,
    routes: [
      { to: '/customers', label: 'Customers' },
      { to: '/contacts', label: 'Contacts' },
      { to: '/accounts', label: 'Accounts' },
      { to: '/deals', label: 'Deals' },
      { to: '/leads', label: 'Leads' },
    ],
  },
  {
    key: 'sales',
    label: 'Sales',
    icon: Target,
    routes: [
      { to: '/proposals', label: 'Proposals' },
      { to: '/sales-orders', label: 'Sales Orders' },
      { to: '/samples', label: 'Samples' },
      { to: '/deal-closure', label: 'Deal Closure' },
      { to: '/discount-requests', label: 'Discount Requests' },
      { to: '/proposal-builder', label: 'Proposal Builder' },
      { to: '/so-po-mapping', label: 'SO→PO Mapping' },
    ],
  },
  {
    key: 'finance',
    label: 'Finance',
    icon: IndianRupee,
    routes: [
      { to: '/invoices', label: 'Invoices' },
      { to: '/payments', label: 'Payments' },
      { to: '/expenses', label: 'Expenses' },
      { to: '/credit-notes', label: 'Credit Notes' },
      { to: '/debit-notes', label: 'Debit Notes' },
      { to: '/accounting', label: 'Ledger Accounting' },
      { to: '/finance/invoice-swap', label: 'Invoice Swap' },
      { to: '/finance/approvals', label: 'Financial Approvals' },
      { to: '/finance/security-deposits', label: 'Security Deposits' },
    ],
  },
  {
    key: 'gst',
    label: 'GST Compliance',
    icon: FileText,
    routes: [
      { to: '/gst/filing', label: 'GST Filing' },
    ],
  },
  {
    key: 'warehouse',
    label: 'Warehouse',
    icon: Warehouse,
    routes: [
      { to: '/inventory', label: 'Stock Position' },
      { to: '/warehouse/dashboard', label: 'Inventory Dashboard' },
      { to: '/warehouse/inward', label: 'Inward Dashboard (GRN)' },
      { to: '/warehouse/dispatch-dashboard', label: 'Dispatch Dashboard' },
      { to: '/warehouse-locations', label: 'Warehouse Locations' },
      { to: '/warehouse/stock-audit', label: 'Stock Audit' },
      { to: '/warehouse/barcode-onboarding', label: 'Barcode Onboarding' },
      { to: '/warehouse/barcode-lifecycle', label: 'Barcode Lifecycle' },
      { to: '/customer-returns', label: 'Customer Returns' },
      { to: '/returns', label: 'Return Dashboard' },
      { to: '/warehouse/cash-rewards', label: 'Cash Reward Stickers' },
      { to: '/dispatch-slips', label: 'Dispatch Slips' },
      { to: '/material-boxes', label: 'Material Boxes' },
    ],
  },
  {
    key: 'procurement',
    label: 'Procurement',
    icon: ShoppingCart,
    routes: [
      { to: '/purchase-orders', label: 'Purchase Orders' },
      { to: '/vendors', label: 'Vendors' },
      { to: '/quotation-jobs', label: 'Quotation Jobs' },
      { to: '/vendor-rate-comparison', label: 'Rate Comparison' },
      { to: '/purchase-requests', label: 'Purchase Requests' },
      { to: '/vendor-returns', label: 'Vendor Returns' },
      { to: '/purchase-contracts', label: 'Purchase Contracts' },
      { to: '/transport-contracts', label: 'Transport Contracts' },
      { to: '/procurement/landed-cost', label: 'Landed Cost Calculator' },
    ],
  },
  {
    key: 'manufacturing',
    label: 'Manufacturing',
    icon: Factory,
    routes: [
      { to: '/manufacturing/dashboard', label: 'Mfg Dashboard' },
      { to: '/manufacturing/boms', label: 'BOMs' },
      { to: '/manufacturing/planning', label: 'Production Planning' },
      { to: '/manufacturing/process-kanban', label: 'Process Kanban' },
      { to: '/manufacturing/qc', label: 'QC Management' },
      { to: '/manufacturing/shift-control', label: 'Shift Control' },
      { to: '/manufacturing/rm-inspection', label: 'RM Inspection' },
      { to: '/manufacturing/fg-activation', label: 'FG Activation' },
      { to: '/manufacturing/floor-inspection', label: 'Floor Inspection' },
      { to: '/manufacturing/alerts', label: 'Alerts' },
      { to: '/manufacturing/scrap', label: 'Scrap Management' },
      { to: '/manufacturing/barcodes', label: 'Barcode Management' },
    ],
  },
  {
    key: 'logistics',
    label: 'Logistics',
    icon: Truck,
    routes: [
      { to: '/transport-partners', label: 'Transport Partners' },
      { to: '/shipments', label: 'Shipments' },
      { to: '/dispatch', label: 'Dispatch' },
      { to: '/logistics/rate-comparison', label: 'Rate Comparison' },
      { to: '/logistics/flow', label: 'Logistics Flow' },
      { to: '/logistics/pipeline', label: 'Delivery Pipeline' },
      { to: '/logistics/tracker', label: 'Post-dispatch Tracker' },
      { to: '/logistics/templates', label: 'WA Template Health' },
      { to: '/dispatch-config', label: 'Dispatch Config' },
      { to: '/payment-gate', label: 'Payment Gate' },
    ],
  },
  {
    key: 'service',
    label: 'Service & Warranty',
    icon: Wrench,
    routes: [
      { to: '/service/sop-dashboard', label: 'Service SOP' },
      { to: '/service-tickets', label: 'Service Tickets' },
      { to: '/service/intake', label: 'Complaint Intake' },
      { to: '/warranties', label: 'Warranties' },
      { to: '/escalation-rules', label: 'Escalation Rules' },
      { to: '/service/itbp-installations', label: 'ITBP Installations' },
      { to: '/service/itbp-reports', label: 'ITBP Reports' },
    ],
  },
  {
    key: 'support',
    label: 'Support',
    icon: Headphones,
    routes: [
      { to: '/support/tickets', label: 'Support Tickets' },
    ],
  },
  {
    key: 'field_partners',
    label: 'Field Partners',
    icon: MapPin,
    routes: [
      { to: '/field-partners', label: 'Directory' },
      { to: '/field-partners/kyc-review', label: 'KYC Review' },
      { to: '/field-partners/claims', label: 'Installation Claims' },
      { to: '/field-partners/incentive-rules', label: 'Incentive Rules' },
      { to: '/field-partners/payouts', label: 'Payouts Console' },
      { to: '/field-partners/fraud-review', label: 'Fraud Review' },
      { to: '/field-partners/config', label: 'Program Config' },
    ],
  },
  {
    key: 'projects',
    label: 'Projects',
    icon: FolderKanban,
    routes: [
      { to: '/projects', label: 'Projects' },
      { to: '/tasks', label: 'Tasks' },
    ],
  },
  {
    key: 'hr',
    label: 'HR',
    icon: Users,
    routes: [
      { to: '/employees', label: 'Employees' },
      { to: '/leave-requests', label: 'Leave Requests' },
      { to: '/incentives', label: 'Incentives' },
    ],
  },
  {
    key: 'automation',
    label: 'Automation',
    icon: Workflow,
    routes: [
      { to: '/workflows', label: 'Workflows' },
    ],
  },
  {
    key: 'ai',
    label: 'AI & Knowledge',
    icon: Sparkles,
    routes: [
      { to: '/ai', label: 'AI Assistant' },
      { to: '/ai/agents', label: 'AI Agents' },
      { to: '/ai/integrations', label: 'API Integrations' },
      { to: '/credits', label: 'AI Credits' },
      { to: '/knowledge-base', label: 'Knowledge Base' },
      { to: '/proposal-builder', label: 'Proposal Builder (AI)' },
    ],
  },
  {
    key: 'telecaller',
    label: 'Telecaller',
    icon: Phone,
    routes: [
      { to: '/telecaller', label: 'Telecaller Today' },
      { to: '/call-logs', label: 'Call Logs' },
      { to: '/offers', label: 'Proposal Tracking' },
      { to: '/team-performance', label: 'Team Performance' },
    ],
  },
  {
    key: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    routes: [
      { to: '/analytics', label: 'Analytics Dashboard' },
    ],
  },
  {
    key: 'integrations',
    label: 'Integrations',
    icon: Plug,
    routes: [
      { to: '/integrations/tally-sync', label: 'Tally Sync' },
      { to: '/settings/integrations', label: 'Ecosystem Integrations' },
    ],
  },
  {
    // Track D — Sun Stellar Pool Heat Pump Calculator. Single-product
    // industrial calculator surfaced via Meeting Mode only (no free access).
    // Module key matches `tenant.enabledModules` entry; enable via Tenant
    // Management → Modules toggle, NOT by tenant slug comparison.
    key: 'pool_heat_pump_calculator',
    label: 'Pool Heat Pump Calculator',
    icon: Calculator,
    routes: [
      { to: '/sales/meeting/start', label: 'Initiate Meeting Mode' },
      { to: '/admin/pool-models', label: 'Models — admin (stock toggle)' },
    ],
  },
];

const VISIBLE_LIMIT = 6;

// ─────────────────────────────────────────────────────────────────────────────

export function ModulesExplorer() {
  const tenant = useAuthStore((s) => s.tenant);
  const [query, setQuery] = useState('');
  const [enabledOnly, setEnabledOnly] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // tenant.enabledModules === null means "all modules" (admin/platform).
  const enabledSet = useMemo(() => {
    if (!tenant) return null; // still loading — treat as all enabled
    if (tenant.enabledModules === null) return null; // null = all
    return new Set(tenant.enabledModules);
  }, [tenant]);

  const isEnabled = (key: string) => enabledSet === null || enabledSet.has(key);

  // Empty state: tenant loaded with an explicit empty enabledModules array
  const noneEnabled =
    !!tenant &&
    Array.isArray(tenant.enabledModules) &&
    tenant.enabledModules.length === 0;

  // Apply search + enabled-only filter
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return MODULE_GROUPS.filter((g) => {
      if (enabledOnly && !isEnabled(g.key)) return false;
      if (!q) return true;
      // search group label OR any route label/path
      if (g.label.toLowerCase().includes(q)) return true;
      return g.routes.some(
        (r) =>
          r.label.toLowerCase().includes(q) ||
          r.to.toLowerCase().includes(q),
      );
    });
    // isEnabled depends on enabledSet; referencing it here to satisfy eslint
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, enabledOnly, enabledSet]);

  if (noneEnabled) {
    return (
      <div>
        <Header />
        <div className="rounded-xl border border-line bg-ivory-1 px-6 py-16 text-center">
          <LayoutGrid size={32} className="mx-auto mb-3 text-ink-3" />
          <h3 className="mb-1 text-base font-semibold text-ink-0">
            No modules enabled for this workspace
          </h3>
          <p className="text-sm text-ink-3">
            Contact your admin to enable modules for your workspace.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />

      {/* Filter bar */}
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-3"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search modules or routes…"
            className="w-full rounded-lg border border-line bg-ivory-0 py-2 pl-9 pr-3 text-sm text-ink-0 placeholder:text-ink-4 focus:border-saffron-600 focus:outline-none focus:ring-1 focus:ring-saffron-600"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-ink-1 select-none">
          <input
            type="checkbox"
            checked={enabledOnly}
            onChange={(e) => setEnabledOnly(e.target.checked)}
            className="h-4 w-4 rounded border-line text-saffron-600 focus:ring-saffron-600"
          />
          Show enabled only
        </label>
      </div>

      {/* Results */}
      {groups.length === 0 ? (
        <div className="rounded-xl border border-line bg-ivory-1 px-6 py-12 text-center">
          <p className="text-sm text-ink-3">
            No modules match your search.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {groups.map((g) => (
            <ModuleCard
              key={g.key}
              group={g}
              enabled={isEnabled(g.key)}
              expanded={!!expanded[g.key]}
              onToggleExpand={() =>
                setExpanded((prev) => ({ ...prev, [g.key]: !prev[g.key] }))
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function Header() {
  return (
    <div className="mb-5">
      <h2 className="text-lg font-semibold text-ink-0">Modules</h2>
      <p className="mt-0.5 text-sm text-ink-3">
        Everything your role can access. Search, filter, and jump to any module
        route in AURA.
      </p>
    </div>
  );
}

interface ModuleCardProps {
  group: ModuleGroup;
  enabled: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
}

function ModuleCard({ group, enabled, expanded, onToggleExpand }: ModuleCardProps) {
  const Icon = group.icon;
  const hasMore = group.routes.length > VISIBLE_LIMIT;
  const visibleRoutes = expanded || !hasMore
    ? group.routes
    : group.routes.slice(0, VISIBLE_LIMIT);
  const hiddenCount = group.routes.length - VISIBLE_LIMIT;

  return (
    <div
      className={`flex flex-col rounded-xl border bg-ivory-1 p-4 transition-colors ${
        enabled ? 'border-line' : 'border-ivory-3 opacity-80'
      }`}
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div
            className={`flex h-9 w-9 items-center justify-center rounded-lg ${
              enabled
                ? 'bg-saffron-50 text-saffron-600'
                : 'bg-ivory-2 text-ink-3'
            }`}
          >
            <Icon size={18} />
          </div>
          <h3 className="text-sm font-semibold text-ink-0">{group.label}</h3>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
            enabled
              ? 'bg-forest-50 text-forest-600'
              : 'bg-ivory-2 text-ink-3'
          }`}
        >
          {enabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>

      <ul className="space-y-1">
        {visibleRoutes.map((r) => (
          <li key={r.to}>
            {enabled ? (
              <Link
                to={r.to}
                className="flex items-center justify-between rounded-md px-2 py-1.5 text-sm text-ink-1 transition-colors hover:bg-ivory-2 hover:text-ink-0"
              >
                <span>{r.label}</span>
                <span className="ml-2 truncate font-mono text-[10px] text-ink-4">
                  {r.to}
                </span>
              </Link>
            ) : (
              <div
                className="flex items-center justify-between rounded-md px-2 py-1.5 text-sm text-ink-3"
                title="Module is disabled for this workspace"
              >
                <span>{r.label}</span>
                <span className="ml-2 truncate font-mono text-[10px] text-ink-4">
                  {r.to}
                </span>
              </div>
            )}
          </li>
        ))}
      </ul>

      {hasMore && (
        <button
          type="button"
          onClick={onToggleExpand}
          className="mt-2 flex items-center justify-center gap-1 rounded-md border border-line px-2 py-1.5 text-xs font-medium text-ink-2 transition-colors hover:bg-ivory-2 hover:text-ink-0"
        >
          {expanded ? (
            <>
              <ChevronUp size={14} /> Show fewer
            </>
          ) : (
            <>
              <ChevronDown size={14} /> See all {group.routes.length} routes
              <span className="text-ink-4">(+{hiddenCount})</span>
            </>
          )}
        </button>
      )}
    </div>
  );
}

export default ModulesExplorer;
